import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { Box, Card, Typography, Chip, Button } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ImageContainer, PlacedElement } from './styles';
import logger from 'utils/logger';
import { getPresignedURL } from 'utils/ReportUtils';
import intl from 'react-intl-universal';
import { useSnackbar } from 'notistack';

const log = logger.createLogger('ReportDndElement');

const propTypes = {
  reportTemplateId: PropTypes.number.isRequired,
  fieldId: PropTypes.string.isRequired,
  imageUrl: PropTypes.string.isRequired,
  pageIndex: PropTypes.number.isRequired,
  onCoordinatesChange: PropTypes.func.isRequired,
  draggableElements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['text', 'image', 'marker']).isRequired,
      label: PropTypes.string.isRequired,
      content: PropTypes.string,
      icon: PropTypes.node,
      imageUrl: PropTypes.string, // Add support for image URLs
    })
  ).isRequired,
  placedElements: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      type: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      content: PropTypes.string,
      imageUrl: PropTypes.string, // Add support for image URLs
    })
  ).isRequired,
  containerWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  containerHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  readOnly: PropTypes.bool,
  title: PropTypes.string,
};

const ReportDndElement = ({
  reportTemplateId,
  fieldId,
  imageUrl,
  onCoordinatesChange,
  draggableElements,
  placedElements,
  containerWidth,
  containerHeight,
  readOnly = false,
  title,
  pageIndex,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedElement, setSelectedElement] = useState(null);
  const [originalImageDimensions, setOriginalImageDimensions] = useState({
    width: 0,
    height: 0,
  });

  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const imgElementRef = useRef(null);
  const elementsLength = useRef(0);
  const [elementUrls, setElementUrls] = useState({});

  // Load original image dimensions
  useEffect(() => {
    const img = new window.Image();
    img.onload = () => {
      setOriginalImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.src = imageUrl;
  }, [imageUrl]);

  useEffect(() => {
    const fetchElementUrls = async () => {
      const urlsToFetch = placedElements
        .filter((element) => element.imagePath && !elementUrls[element.id])
        .map(async (element) => {
          try {
            // You'll need to pass the reportTemplateId here - this should come from props
            const url = await getPresignedURL(
              reportTemplateId,
              element.imagePath
            );
            return { id: element.id, url };
          } catch (error) {
            log.error('Failed to fetch URL for element:', element.id, error);
            return { id: element.id, url: null };
          }
        });

      if (urlsToFetch.length > 0) {
        const results = await Promise.all(urlsToFetch);
        const newUrls = {};
        results.forEach(({ id, url }) => {
          if (url) {
            newUrls[id] = url;
          }
        });
        setElementUrls((prev) => ({ ...prev, ...newUrls }));
      }
    };

    fetchElementUrls();
  }, [placedElements, reportTemplateId]);

  const getImageCoordinates = useCallback(
    (clientX, clientY) => {
      if (
        !imgElementRef.current ||
        !originalImageDimensions.width ||
        !originalImageDimensions.height
      ) {
        return { x: 0, y: 0 };
      }

      const imgRect = imgElementRef.current.getBoundingClientRect();
      const naturalWidth = originalImageDimensions.width;
      const naturalHeight = originalImageDimensions.height;

      // Calculate the actual displayed dimensions of the image due to object-fit: contain
      let displayedImageWidth;
      let displayedImageHeight;
      let offsetX = 0;
      let offsetY = 0;

      const imgAspectRatio = naturalWidth / naturalHeight;
      const containerAspectRatio = imgRect.width / imgRect.height;

      if (imgAspectRatio > containerAspectRatio) {
        // Image is wider than container, height is constrained
        displayedImageWidth = imgRect.width;
        displayedImageHeight = imgRect.width / imgAspectRatio;
        offsetY = (imgRect.height - displayedImageHeight) / 2; // Calculate vertical offset
      } else {
        // Image is taller than container, width is constrained
        displayedImageHeight = imgRect.height;
        displayedImageWidth = imgRect.height * imgAspectRatio;
        offsetX = (imgRect.width - displayedImageWidth) / 2; // Calculate horizontal offset
      }

      // Calculate click position relative to the displayed *image content* (excluding padding)
      const clickXRelativeToImage = clientX - imgRect.left - offsetX;
      const clickYRelativeToImage = clientY - imgRect.top - offsetY;

      // Convert to percentage of the *original image* dimensions
      const xPercent = (clickXRelativeToImage / displayedImageWidth) * 100;
      const yPercent = (clickYRelativeToImage / displayedImageHeight) * 100;

      // Ensure coordinates are within 0-100%
      const finalX = Math.max(0, Math.min(100, xPercent));
      const finalY = Math.max(0, Math.min(100, yPercent));

      return { x: finalX, y: finalY };
    },
    [originalImageDimensions]
  );

  const handleImageClick = useCallback(
    (event) => {
      if (readOnly) return;

      if (!selectedElement) {
        enqueueSnackbar(
          intl.get(
            'issue_details_report_dnd_text_select_an_element_type_first'
          ),
          {
            variant: 'warning',
          }
        );
        return;
      }

      const currentSelectedElement = draggableElements.find(
        (element) => element.id === selectedElement.id
      );

      if (currentSelectedElement) {
        const { x, y } = getImageCoordinates(event.clientX, event.clientY);
        const newPlacedElement = {
          id: `${Date.now()}-${elementsLength.current++}`,
          x,
          y,
          type: currentSelectedElement.type,
          label: currentSelectedElement.label,
          content: currentSelectedElement.content || '',
          imagePath: currentSelectedElement.imagePath || '',
          pageIndex: pageIndex,
          imageSettings: {
            width: currentSelectedElement.width,
            height: currentSelectedElement.height,
          },
        };
        onCoordinatesChange(fieldId, [...placedElements, newPlacedElement]);
      }
    },
    [
      readOnly,
      selectedElement,
      draggableElements,
      placedElements,
      onCoordinatesChange,
      getImageCoordinates,
      fieldId,
      pageIndex,
      enqueueSnackbar,
    ]
  );

  const handleElementSelect = (element) => {
    setSelectedElement(
      selectedElement && selectedElement.id === element.id ? null : element
    );
  };

  const handleRemoveElement = (elementId) => {
    const updatedElements = placedElements.filter(
      (element) => element.id !== elementId
    );
    onCoordinatesChange(fieldId, updatedElements);
  };

  const renderPlacedElement = (element) => {
    if (element.imagePath) {
      const imageUrl = elementUrls[element.id];
      if (imageUrl) {
        return (
          <img
            src={imageUrl}
            alt={element.label}
            style={{
              width: 'auto',
              height: 'auto',
              maxWidth: '20px',
              maxHeight: '20px',
              objectFit: 'contain',
              borderRadius: '4px',
            }}
          />
        );
      } else {
        // Show loading state or placeholder while URL is being fetched
        return (
          <Box
            sx={{
              width: '20px',
              height: '20px',
              backgroundColor: 'grey.300',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Typography variant="caption" sx={{ fontSize: '8px' }}>
              ...
            </Typography>
          </Box>
        );
      }
    }
    return null;
  };

  const groupedElements = draggableElements.reduce((acc, element) => {
    acc.push(element);
    return acc;
  }, []);

  const getDisplayedElementPosition = useCallback(
    (xPercent, yPercent) => {
      if (
        !imgElementRef.current ||
        !originalImageDimensions.width ||
        !originalImageDimensions.height
      ) {
        return { left: '0%', top: '0%' };
      }

      const imgRect = imgElementRef.current.getBoundingClientRect();
      const naturalWidth = originalImageDimensions.width;
      const naturalHeight = originalImageDimensions.height;

      let displayedImageWidth;
      let displayedImageHeight;
      let offsetX = 0;
      let offsetY = 0;

      const imgAspectRatio = naturalWidth / naturalHeight;
      const containerAspectRatio = imgRect.width / imgRect.height;

      if (imgAspectRatio > containerAspectRatio) {
        displayedImageWidth = imgRect.width;
        displayedImageHeight = imgRect.width / imgAspectRatio;
        offsetY = (imgRect.height - displayedImageHeight) / 2;
      } else {
        displayedImageHeight = imgRect.height;
        displayedImageWidth = imgRect.height * imgAspectRatio;
        offsetX = (imgRect.width - displayedImageWidth) / 2;
      }

      // Convert percentage from original image to pixels on the displayed image
      const pixelXRelativeToDisplayedImage =
        (xPercent / 100) * displayedImageWidth;
      const pixelYRelativeToDisplayedImage =
        (yPercent / 100) * displayedImageHeight;

      // Add back the offset to get the position relative to the imgElementRef.current (which is the actual <img> tag)
      const finalPixelX = pixelXRelativeToDisplayedImage + offsetX;
      const finalPixelY = pixelYRelativeToDisplayedImage + offsetY;

      // Convert pixels back to percentages relative to the imgRect.width/height for positioning
      // This is because PlacedElement uses percentage positioning within ImageContainer (which is imgRect dimensions)
      const leftPercent = (finalPixelX / imgRect.width) * 100;
      const topPercent = (finalPixelY / imgRect.height) * 100;

      return { left: `${leftPercent}%`, top: `${topPercent}%` };
    },
    [originalImageDimensions]
  );

  return (
    <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
      <Card sx={{ p: 2 }}>
        <Box>
          <Typography variant="body2" gutterBottom>
            {title}
          </Typography>
        </Box>

        {/* Element Type Selection */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            gutterBottom
            sx={{ mb: 2 }}>
            {intl.get(
              'issue_details_report_dnd_text_select_an_element_type_above'
            )}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              flexWrap: 'wrap',
              mt: 2,
            }}>
            {groupedElements.map((element) => (
              <Button
                key={element.id}
                variant={
                  selectedElement && selectedElement.id === element.id
                    ? 'contained'
                    : 'outlined'
                }
                startIcon={
                  element.imageUrl ? (
                    <img
                      src={element.imageUrl}
                      alt={element.label}
                      style={{
                        width: '16px',
                        height: '16px',
                        objectFit: 'contain',
                        borderRadius: '2px',
                      }}
                    />
                  ) : null
                }
                size="small"
                onClick={() => handleElementSelect(element)}
                sx={{
                  textTransform: 'capitalize',
                  minWidth: '80px',
                  color:
                    selectedElement && selectedElement.id === element.id
                      ? '#FFF'
                      : '#000',
                }}>
                {element.label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Image Area Section */}
        <Box>
          {!readOnly && selectedElement && (
            <Typography variant="caption" color="primary.main" sx={{ mt: 2 }}>
              {`(${intl.get(
                'issue_details_report_dnd_text_click_to_add_elements',
                {
                  elementType: selectedElement.label,
                }
              )})`}
            </Typography>
          )}

          {/* Placed Elements Summary */}
          {placedElements.length > 0 && (
            <Grid container spacing={1} mb={2}>
              {placedElements.map((element) => {
                const currentIndex = element.id.split('-')[1];
                return (
                  <Grid key={element.id}>
                    <Chip
                      label={`${currentIndex}. ${element.label}`}
                      color="info"
                      variant="outlined"
                      size="small"
                      onDelete={
                        !readOnly
                          ? () => handleRemoveElement(element.id)
                          : undefined
                      }
                    />
                  </Grid>
                );
              })}
            </Grid>
          )}
          <Box
            ref={containerRef}
            mt={1}
            sx={{
              width: '100%',
              height: 'auto',
              position: 'relative',
              overflow: 'auto',
              cursor: selectedElement ? 'crosshair' : 'default',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            <DragDropContext onDragEnd={() => {}}>
              <Droppable droppableId="image-drop-zone">
                {(provided) => (
                  <ImageContainer
                    ref={(el) => {
                      provided.innerRef(el);
                      imageRef.current = el;
                    }}
                    {...provided.droppableProps}
                    onClick={handleImageClick}
                    sx={{
                      position: 'relative',
                      width: containerWidth,
                      height: containerHeight,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}>
                    <img
                      ref={imgElementRef}
                      src={imageUrl}
                      alt="Background"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        pointerEvents: 'none',
                        userSelect: 'none',
                      }}
                    />
                    {placedElements.map((element) => {
                      const { left, top } = getDisplayedElementPosition(
                        element.x,
                        element.y
                      );
                      return (
                        <PlacedElement
                          key={element.id}
                          sx={{
                            position: 'absolute',
                            left: left,
                            top: top,
                            transform: 'translate(-50%, -50%)',
                            pointerEvents: 'auto',
                            transition: 'none !important',
                            '&:hover': {
                              transform: 'translate(-50%, -50%) !important',
                              boxShadow: 'none !important',
                              zIndex: 10,
                            },
                          }}>
                          {renderPlacedElement(element)}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                              backgroundColor: 'primary.main',
                              color: 'white',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              border: '2px solid white',
                              zIndex: 15,
                            }}>
                            {element.id.split('-')[1]}
                          </Box>
                        </PlacedElement>
                      );
                    })}
                    {provided.placeholder}
                  </ImageContainer>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

ReportDndElement.propTypes = propTypes;

export default ReportDndElement;
